import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { useTrace } from '../context/TraceContext';
import { useApp } from '../context/AppContext';
import TraceLabel from '../components/trace/TraceLabel';
import ReportExportButton from '../components/export/ReportExportButton';
import ReportSection from '../components/export/ReportSection';
import { 
  Package, Building2, AlertTriangle, CheckCircle2,
  Plus, ChevronRight
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { materials, suppliers, formatDate } from '../data/mockData';

const MaterialsPage = () => {
  const { pidTraceMode } = useTrace();
  const { isReadOnly } = useApp();

  const getMaterialStatusColor = (status) => {
    switch (status) {
      case 'Released': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'Quarantine': return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'Rejected': return 'bg-red-50 text-red-700 border-red-200';
      default: return 'bg-slate-100 text-slate-600 border-slate-200';
    }
  };

  const getSupplierRatingColor = (rating) => {
    switch (rating) {
      case 'A': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'B': return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'C': return 'bg-red-50 text-red-700 border-red-200';
      default: return 'bg-slate-100 text-slate-600 border-slate-200';
    }
  };

  return (
    <div className="space-y-6" data-testid="materials-page">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Materials & Suppliers
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Material receipt, quarantine, release workflow and supplier management
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* <ReportExportButton 
            title="Materials & Suppliers Report"
            subtitle="Material receipt, quarantine, release workflow and supplier management"
          /> */}
        </div>
      </div>

      <Tabs defaultValue="materials">
        <TabsList className="bg-slate-100 border border-slate-200">
          <TabsTrigger value="materials" className="data-[state=active]:bg-[#fd4816] data-[state=active]:text-white">
            <Package className="h-4 w-4 mr-2" />
            Materials
          </TabsTrigger>
          <TabsTrigger value="suppliers" className="data-[state=active]:bg-[#fd4816] data-[state=active]:text-white">
            <Building2 className="h-4 w-4 mr-2" />
            Suppliers
          </TabsTrigger>
        </TabsList>

        <TabsContent value="materials" className="mt-4 space-y-4">
          <ReportSection id="materials-list" title="Materials List" description="Material receipt, quarantine, and release status">
            <Card className={`bg-white border border-slate-200 shadow-sm relative ${pidTraceMode ? 'border-cyan-300' : ''}`}>
              <TraceLabel prd="PRD §6.9" req="SM-MAT-001" />
              <Table>
              <TableHeader>
                <TableRow className="border-slate-200 hover:bg-transparent">
                  <TableHead className="text-slate-600">ID</TableHead>
                  <TableHead className="text-slate-600">Material</TableHead>
                  <TableHead className="text-slate-600">Supplier</TableHead>
                  <TableHead className="text-slate-600">Status</TableHead>
                  <TableHead className="text-slate-600">COA Status</TableHead>
                  <TableHead className="text-slate-600">Lot No</TableHead>
                  <TableHead className="text-slate-600">Qty</TableHead>
                  <TableHead className="text-slate-600">Expiry</TableHead>
                  <TableHead className="text-slate-600 w-8"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {materials.map(mat => (
                  <TableRow key={mat.id} className="hover:bg-slate-50 cursor-pointer" data-testid={`material-row-${mat.id}`}>
                    <TableCell className="font-mono text-xs text-slate-500">{mat.id}</TableCell>
                    <TableCell className="text-sm text-slate-800 font-medium">{mat.name}</TableCell>
                    <TableCell className="text-sm text-slate-600">{mat.supplier}</TableCell>
                    <TableCell>
                      <Badge className={`${getMaterialStatusColor(mat.status)} text-xs`}>{mat.status}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={`${mat.coaStatus === 'Approved' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'} text-xs`}>
                        {mat.coaStatus}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-mono text-xs text-slate-500">{mat.lotNo}</TableCell>
                    <TableCell className="text-sm text-slate-600">{mat.receivedQty}</TableCell>
                    <TableCell className="text-sm text-slate-600">{formatDate(mat.expiryDate)}</TableCell>
                    <TableCell>
                      <ChevronRight className="h-4 w-4 text-slate-400" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
          </ReportSection>
        </TabsContent>

        <TabsContent value="suppliers" className="mt-4 space-y-4">
          <div className="flex justify-end">
            {!isReadOnly && (
              <Button className="bg-[#fd4816] hover:bg-[#ea380c] text-white" data-testid="add-supplier">
                <Plus className="h-4 w-4 mr-2" />
                Add Supplier
              </Button>
            )}
          </div>

          <ReportSection id="suppliers-list" title="Suppliers List" description="Supplier management and ratings">
            <Card className={`bg-white border border-slate-200 shadow-sm relative ${pidTraceMode ? 'border-cyan-300' : ''}`}>
              <TraceLabel prd="PRD §6.10" req="SM-SUP-001" />
              <Table>
              <TableHeader>
                <TableRow className="border-slate-200 hover:bg-transparent">
                  <TableHead className="text-slate-600">ID</TableHead>
                  <TableHead className="text-slate-600">Supplier Name</TableHead>
                  <TableHead className="text-slate-600">Status</TableHead>
                  <TableHead className="text-slate-600">Rating</TableHead>
                  <TableHead className="text-slate-600">Last Audit</TableHead>
                  <TableHead className="text-slate-600">Next Audit</TableHead>
                  <TableHead className="text-slate-600">Materials</TableHead>
                  <TableHead className="text-slate-600 w-8"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {suppliers.map(sup => (
                  <TableRow key={sup.id} className="hover:bg-slate-50 cursor-pointer" data-testid={`supplier-row-${sup.id}`}>
                    <TableCell className="font-mono text-xs text-slate-500">{sup.id}</TableCell>
                    <TableCell className="text-sm text-slate-800 font-medium">{sup.name}</TableCell>
                    <TableCell>
                      <Badge className={`${sup.status === 'Approved' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'} text-xs`}>
                        {sup.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={`${getSupplierRatingColor(sup.rating)} text-xs font-bold`}>
                        {sup.rating}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">{formatDate(sup.lastAudit)}</TableCell>
                    <TableCell className="text-sm text-slate-600">{formatDate(sup.nextAudit)}</TableCell>
                    <TableCell className="text-xs text-slate-500">{sup.materials.join(', ')}</TableCell>
                    <TableCell>
                      <ChevronRight className="h-4 w-4 text-slate-400" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
          </ReportSection>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default MaterialsPage;
